import React, { useState, useEffect } from 'react';
import { Form, Input, Button, Table, Modal, message, Spin, Card, Alert, Typography, Select, Tag, Tooltip, Switch, Popconfirm } from 'antd';
import { PlusOutlined, CopyOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { adminAPI } from '../api/admin';
import { sanitizePhoneInput } from '../utils/phone';

const { Paragraph, Text } = Typography;

const ENTRY_SOURCE_LABELS = {
  admin_direct_invite: { label: 'دعوت مستقیم', color: 'purple' },
  waitlist_auto_invite: { label: 'از لیست انتظار', color: 'blue' }
};

export default function InvitationManager() {
  const [form] = Form.useForm();
  const [invitations, setInvitations] = useState([]);
  const [schedules, setSchedules] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [lastCreated, setLastCreated] = useState(null);
  const [pagination, setPagination] = useState({ page: 1, limit: 10, total: 0 });
  const [reassign, setReassign] = useState(null); // { invitation, scheduleId }
  const [editingInvitation, setEditingInvitation] = useState(null);
  const [editSaving, setEditSaving] = useState(false);
  const [editForm] = Form.useForm();

  const loadSchedules = () => {
    adminAPI.getTrainSchedules().then((res) => {
      if (res.data.success) setSchedules(res.data.schedules || []);
    }).catch(() => {});
  };

  useEffect(() => {
    loadInvitations();
    loadSchedules();
  }, []);

  const loadInvitations = async (page = 1) => {
    setLoading(true);
    try {
      const response = await adminAPI.getInvitations(page, 10);
      if (response.data.success) {
        setInvitations(response.data.invitations);
        setPagination({
          page,
          limit: 10,
          total: response.data.pagination.total
        });
      }
    } catch (error) {
      message.error('خطا در بارگذاری دعوت‌نامه‌ها');
    } finally {
      setLoading(false);
    }
  };

  const onFinish = async (values) => {
    setLoading(true);
    try {
      const response = await adminAPI.createInvitation({
        firstName: values.firstName,
        lastName: values.lastName,
        phoneNumber: values.phoneNumber || undefined,
        addressLine1: values.addressLine1,
        addressLine2: values.addressLine2,
        addressLine3: values.addressLine3,
        trainScheduleId: values.trainScheduleId || undefined,
        templateName: 'invitation'
      });

      if (response.data.success) {
        const smsStatus = response.data.invitation?.smsStatus;
        if (!values.phoneNumber) {
          message.success('دعوت‌نامه ایجاد شد — لینک را دستی ارسال کنید');
        } else if (smsStatus === 'sent') {
          message.success('دعوت‌نامه ایجاد و پیامک ارسال شد!');
        } else {
          message.warning(response.data.message || 'دعوت‌نامه ایجاد شد ولی ارسال پیامک ناموفق بود');
        }
        setLastCreated(response.data.invitation);
        form.resetFields();
        setIsModalVisible(false);
        loadInvitations();
        loadSchedules();
      }
    } catch (error) {
      message.error(error.response?.data?.message || 'خطا در ایجاد دعوت‌نامه');
    } finally {
      setLoading(false);
    }
  };

  const resendSms = async (record) => {
    try {
      const response = await adminAPI.resendInvitationSms(record._id);
      if (response.data.smsStatus === 'sent') {
        message.success(response.data.message);
      } else {
        message.warning(response.data.message);
      }
      loadInvitations(pagination.page);
    } catch (error) {
      message.error(error.response?.data?.message || 'خطا در ارسال مجدد پیامک');
    }
  };

  const copyLink = (link) => {
    navigator.clipboard.writeText(link);
    message.success('لینک دعوت‌نامه کپی شد');
  };

  const openEdit = (record) => {
    setEditingInvitation(record);
    editForm.setFieldsValue({
      recipientFirstName: record.recipientFirstName || '',
      recipientLastName: record.recipientLastName || '',
      recipientPhone: record.recipientPhone || '',
      addressLine1: record.addressLine1 || '',
      addressLine2: record.addressLine2 || '',
      addressLine3: record.addressLine3 || '',
      platform: record.platform || ''
    });
  };

  const saveEdit = async () => {
    try {
      const values = await editForm.validateFields();
      setEditSaving(true);
      await adminAPI.updateInvitation(editingInvitation._id, {
        ...values,
        recipientPhone: values.recipientPhone || null
      });
      message.success('دعوت‌نامه ویرایش شد');
      setEditingInvitation(null);
      loadInvitations(pagination.page);
    } catch (err) {
      if (err?.errorFields) return; // antd validation error, already shown inline
      message.error(err.response?.data?.message || 'خطا در ویرایش دعوت‌نامه');
    } finally {
      setEditSaving(false);
    }
  };

  const toggleActive = async (record) => {
    try {
      const res = await adminAPI.toggleInvitation(record._id);
      message.success(res.data.message);
      loadInvitations(pagination.page);
    } catch (err) {
      message.error(err.response?.data?.message || 'خطا در تغییر وضعیت دعوت‌نامه');
    }
  };

  const removeInvitation = async (record) => {
    try {
      const res = await adminAPI.deleteInvitation(record._id);
      message.success(res.data.message || 'دعوت‌نامه حذف شد');
      loadInvitations(pagination.page);
    } catch (err) {
      message.error(err.response?.data?.message || 'خطا در حذف دعوت‌نامه');
    }
  };

  const columns = [
    {
      title: 'نام',
      render: (_, record) => `${record.recipientFirstName} ${record.recipientLastName}`
    },
    {
      title: 'شماره تماس',
      dataIndex: 'recipientPhone'
    },
    {
      title: 'کد دعوت',
      dataIndex: 'code',
      render: (code) => <code>{code}</code>
    },
    {
      title: 'کد بلیت',
      dataIndex: 'ticketCode',
      render: (code) => <code>{code}</code>
    },
    {
      title: 'منبع ورود',
      dataIndex: 'entrySource',
      render: (v) => {
        const info = ENTRY_SOURCE_LABELS[v] || { label: v || '—', color: 'default' };
        return <Tag color={info.color}>{info.label}</Tag>;
      }
    },
    {
      title: 'حرکت قطار',
      render: (_, record) => (
        <Button size="small" onClick={() => setReassign({ invitation: record, scheduleId: record.trainScheduleId || undefined })}>
          {record.departureDate ? new Date(record.departureDate).toLocaleString('fa-IR') : 'تعیین زمان'}
        </Button>
      )
    },
    {
      title: 'لینک',
      render: (_, record) => {
        const link = `${window.location.origin}/invitation?code=${record.code}`;
        return <Button size="small" icon={<CopyOutlined />} onClick={() => copyLink(link)}>کپی لینک</Button>;
      }
    },
    {
      title: 'وضعیت',
      render: (_, record) => record.isUsed ? '✅ استفاده شده' : '⏳ منتظر'
    },
    {
      title: 'بازدید لینک',
      render: (_, record) => {
        if (!record.firstViewedAt) return <Tag color="default">باز نشده</Tag>;
        const first = new Date(record.firstViewedAt).toLocaleString('fa-IR');
        const label = record.viewCount > 1 ? `${first} (${record.viewCount} بار)` : first;
        return (
          <Tooltip title={`اولین بازدید: ${first}${record.lastViewedAt ? ` — آخرین بازدید: ${new Date(record.lastViewedAt).toLocaleString('fa-IR')}` : ''}`}>
            <Tag color="green">✔ {label}</Tag>
          </Tooltip>
        );
      }
    },
    {
      title: 'SMS',
      render: (_, record) => {
        const statusMap = {
          'sent': '✅ ارسال شده',
          'pending': '⏳ منتظر',
          'failed': '❌ ناموفق'
        };
        const label = record.smsStatus === 'failed' && record.smsError
          ? <Tooltip title={record.smsError}>{statusMap[record.smsStatus]}</Tooltip>
          : (statusMap[record.smsStatus] || record.smsStatus);
        return (
          <span>
            {label}
            {record.smsStatus === 'failed' && record.recipientPhone && (
              <Button size="small" type="link" onClick={() => resendSms(record)}>ارسال مجدد</Button>
            )}
          </span>
        );
      }
    },
    {
      title: 'فعال',
      key: 'isActive',
      render: (_, record) => (
        <Switch
          size="small"
          checked={record.isActive !== false}
          onChange={() => toggleActive(record)}
        />
      )
    },
    {
      title: 'ویرایش',
      key: 'edit',
      render: (_, record) => (
        <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(record)} />
      )
    },
    {
      title: 'حذف',
      key: 'delete',
      render: (_, record) => (
        record.isUsed ? (
          <Tooltip title="دعوت‌نامه‌ی استفاده‌شده قابل حذف نیست">
            <Button size="small" danger icon={<DeleteOutlined />} disabled />
          </Tooltip>
        ) : (
          <Popconfirm
            title="حذف این دعوت‌نامه؟"
            description="این عملیات قابل بازگشت نیست."
            onConfirm={() => removeInvitation(record)}
            okText="حذف"
            cancelText="انصراف"
          >
            <Button size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        )
      )
    }
  ];

  return (
    <Card title="مدیریت دعوت‌نامه‌ها">
      <Button
        type="primary"
        icon={<PlusOutlined />}
        onClick={() => setIsModalVisible(true)}
        style={{ marginBottom: '16px' }}
      >
        دعوت‌نامه جدید
      </Button>

      {lastCreated && lastCreated.invitationLink && (
        <Alert
          type="success"
          style={{ marginBottom: 16 }}
          closable
          onClose={() => setLastCreated(null)}
          message={`دعوت‌نامه برای ${lastCreated.recipient} صادر شد`}
          description={
            <div>
              <Paragraph copyable={{ text: lastCreated.invitationLink }} style={{ marginBottom: 4 }}>
                <Text strong>لینک شخصی: </Text>
                <Text code>{lastCreated.invitationLink}</Text>
              </Paragraph>
              <Text type="secondary">
                گیرنده با وارد کردن شماره‌ی خود ({lastCreated.phone}) می‌تواند دعوت‌نامه را ببیند.
                حرکت قطار: {lastCreated.departureDate ? new Date(lastCreated.departureDate).toLocaleDateString('fa-IR') : ''} ساعت ۱۱:۰۰
              </Text>
            </div>
          }
        />
      )}

      <Spin spinning={loading}>
        <Table
          columns={columns}
          dataSource={invitations}
          rowKey="_id"
          pagination={{
            pageSize: pagination.limit,
            total: pagination.total,
            current: pagination.page,
            onChange: loadInvitations
          }}
        />
      </Spin>

      <Modal
        title="ایجاد دعوت‌نامه جدید"
        visible={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
        footer={null}
      >
        <Form form={form} onFinish={onFinish} layout="vertical">
          <Form.Item
            name="firstName"
            label="نام (اختیاری — اگر خالی بماند، خود کاربر هنگام مطالعه‌ی نامه وارد می‌کند)"
          >
            <Input placeholder="مثال: هری" />
          </Form.Item>

          <Form.Item
            name="lastName"
            label="نام خانوادگی (اختیاری)"
          >
            <Input placeholder="مثال: پاتر" />
          </Form.Item>

          <Form.Item
            name="phoneNumber"
            label="شماره تماس (اختیاری — اگر خالی بماند، اولین کسی که با این لینک OTP بگیرد مالک دائمی آن می‌شود)"
            normalize={sanitizePhoneInput}
          >
            <Input placeholder="09123456789" />
          </Form.Item>

          <Form.Item name="addressLine1" label="آدرس - خط ۱">
            <Input placeholder="مثال: خیابان دیاگون، پلاک ۹۳" />
          </Form.Item>

          <Form.Item name="addressLine2" label="آدرس - خط ۲">
            <Input placeholder="مثال: محله‌ی جادوگران، لندن" />
          </Form.Item>

          <Form.Item name="addressLine3" label="آدرس - خط ۳">
            <Input placeholder="مثال: بریتانیا" />
          </Form.Item>

          <Form.Item
            name="trainScheduleId"
            label="زمان حرکت قطار"
            extra="اگر خالی بماند، خودکار زودترین قطار فعال و دارای جا از پنل ادمین انتخاب می‌شود."
          >
            <Select
              allowClear
              placeholder="انتخاب از زمان‌های تعریف‌شده (یا خالی برای انتخاب خودکار)"
              options={schedules.map((s) => ({
                value: s._id,
                disabled: !!s.isFull,
                label: `${s.label} — ${new Date(s.departureAt).toLocaleString('fa-IR')}${
                  s.capacity != null ? ` (${s.isFull ? 'پر شده' : `${s.remainingCapacity} جای خالی از ${s.capacity}`})` : ''
                }`
              }))}
            />
          </Form.Item>

          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
            message="این کد دعوت هرگز منقضی نمی‌شود."
          />

          <Button type="primary" htmlType="submit" block loading={loading}>
            ایجاد و ارسال
          </Button>
        </Form>
      </Modal>

      <Modal
        title="تغییر زمان حرکت قطار این دعوت‌نامه"
        open={!!reassign}
        onCancel={() => setReassign(null)}
        onOk={async () => {
          try {
            await adminAPI.assignTrainScheduleToInvitation(reassign.invitation._id, reassign.scheduleId);
            message.success('زمان حرکت به‌روز شد');
            setReassign(null);
            loadInvitations(pagination.page);
            loadSchedules();
          } catch {
            message.error('خطا در به‌روزرسانی زمان حرکت');
          }
        }}
        okText="ذخیره"
        cancelText="انصراف"
      >
        <Select
          style={{ width: '100%' }}
          placeholder="انتخاب زمان حرکت"
          value={reassign?.scheduleId}
          onChange={(v) => setReassign((r) => ({ ...r, scheduleId: v }))}
          options={schedules.map((s) => ({
            value: s._id,
            disabled: !!s.isFull && s._id !== reassign?.invitation?.trainScheduleId,
            label: `${s.label} — ${new Date(s.departureAt).toLocaleString('fa-IR')}${
              s.capacity != null ? ` (${s.isFull ? 'پر شده' : `${s.remainingCapacity} جای خالی از ${s.capacity}`})` : ''
            }`
          }))}
        />
      </Modal>

      <Modal
        title="ویرایش دعوت‌نامه"
        open={!!editingInvitation}
        onCancel={() => setEditingInvitation(null)}
        onOk={saveEdit}
        confirmLoading={editSaving}
        okText="ذخیره"
        cancelText="انصراف"
        destroyOnClose
      >
        <Form form={editForm} layout="vertical">
          <Form.Item
            name="recipientFirstName"
            label="نام (اختیاری)"
          >
            <Input placeholder="مثال: هری" />
          </Form.Item>

          <Form.Item
            name="recipientLastName"
            label="نام خانوادگی (اختیاری)"
          >
            <Input placeholder="مثال: پاتر" />
          </Form.Item>

          <Form.Item name="recipientPhone" label="شماره تماس" normalize={sanitizePhoneInput}>
            <Input placeholder="09123456789" style={{ direction: 'ltr' }} />
          </Form.Item>

          <Form.Item name="addressLine1" label="آدرس - خط ۱">
            <Input placeholder="مثال: خیابان دیاگون، پلاک ۹۳" />
          </Form.Item>

          <Form.Item name="addressLine2" label="آدرس - خط ۲">
            <Input placeholder="مثال: محله‌ی جادوگران، لندن" />
          </Form.Item>

          <Form.Item name="addressLine3" label="آدرس - خط ۳">
            <Input placeholder="مثال: بریتانیا" />
          </Form.Item>

          <Form.Item name="platform" label="سکو">
            <Input placeholder="9¾" />
          </Form.Item>

          {editingInvitation?.claimedAt && (
            <Alert
              type="warning"
              showIcon
              style={{ marginBottom: 8 }}
              message="این دعوت‌نامه قبلاً توسط گیرنده تایید شده — تغییر شماره تماس به مدیر اطلاع داده می‌شود."
            />
          )}
        </Form>
      </Modal>
    </Card>
  );
}
